import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth";
import { useClosures } from "../lib/closures";
import { useOnline, useScrolled } from "../lib/hooks";
import { useDashboard } from "../lib/queries";
import { Icon, type IconName } from "./Icon";
import { api } from "../lib/api";
import { Button, IconButton } from "./ui";

/** Retour arrière : historique de l'application si possible, sinon page parente. */
export function useBack(fallback: string) {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    if (location.key !== "default" && window.history.length > 1) navigate(-1);
    else navigate(fallback, { replace: true });
  };
}

export function TopBar({
  title,
  back,
  large,
  actions
}: {
  title: ReactNode;
  back?: string;
  large?: boolean;
  actions?: ReactNode;
}) {
  const scrolled = useScrolled();
  const goBack = useBack(back ?? "/");
  return (
    <header className={`topbar ${scrolled ? "scrolled" : ""}`}>
      <div className={`topbar-inner ${back ? "has-back" : ""}`}>
        {back && <IconButton icon="back" label="Retour" onClick={goBack} />}
        <h1 className={`topbar-title ${large && !scrolled ? "large" : ""}`}>{title}</h1>
        {actions}
      </div>
    </header>
  );
}

export function Page({ children, noNav }: { children: ReactNode; noNav?: boolean }) {
  return <main className={`page ${noNav ? "no-nav" : ""}`}>{children}</main>;
}

const NAV: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: "/", label: "Accueil", icon: "home", end: true },
  { to: "/planning", label: "Planning", icon: "calendar" },
  { to: "/clients", label: "Clients", icon: "building" },
  { to: "/maintenance", label: "Maintenance", icon: "wrench" },
  { to: "/more", label: "Plus", icon: "menu" }
];

export function BottomNav() {
  const dashboard = useDashboard();
  const overdue = dashboard.data?.overdue.length ?? 0;
  return (
    <nav className="bottom-nav" aria-label="Navigation principale">
      <ul>
        {NAV.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} end={item.end} className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="nav-icon">
                <Icon name={item.icon} />
              </span>
              {item.label}
              {item.to === "/maintenance" && overdue > 0 && (
                <span className="nav-badge" aria-label={`${overdue} en retard`}>
                  {overdue > 99 ? "99+" : overdue}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Fab({ to, label, icon = "plus" }: { to: string; label: string; icon?: IconName }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="fab" onClick={() => navigate(to)}>
      <Icon name={icon} size={24} />
      {label}
    </button>
  );
}

export function StatusBanners({ needRefresh, onUpdate }: { needRefresh: boolean; onUpdate: () => void }) {
  const online = useOnline();
  const { offline } = useAuth();
  const disconnected = !online || offline;
  const ref = useRef<HTMLDivElement>(null);

  // La barre du haut (et ce qui colle dessous) se place sous les bandeaux : leur hauteur est publiée en --banners-h.
  useLayoutEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return;
    const measure = () => {
      const height = el.childElementCount ? el.offsetHeight : 0;
      root.style.setProperty("--banners-h", `${height}px`);
      root.classList.toggle("has-banners", height > 0);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    const mutations = new MutationObserver(measure);
    mutations.observe(el, { childList: true });
    return () => {
      observer?.disconnect();
      mutations.disconnect();
      root.style.removeProperty("--banners-h");
      root.classList.remove("has-banners");
    };
  }, []);

  return (
    <>
      <div className="banners" ref={ref}>
        {disconnected && (
          <div className="banner offline" role="status">
            <Icon name="wifiOff" size={18} />
            Hors connexion : affichage des dernières données reçues.
          </div>
        )}
        <ClosureBanners />
      </div>
      {!disconnected && needRefresh && <UpdatePrompt onUpdate={onUpdate} />}
    </>
  );
}

/** Bons faits sur ce téléphone et pas encore acceptés par le serveur. */
function ClosureBanners() {
  const { items } = useClosures();
  const waiting = items.filter((item) => item.state !== "refused").length;
  const refused = items.filter((item) => item.state === "refused");
  return (
    <>
      {waiting > 0 && (
        <div className="banner closures" role="status">
          <Icon name="clock" size={18} />
          {waiting > 1 ? `${waiting} bons en attente d'envoi` : "1 bon en attente d'envoi"}
        </div>
      )}
      {refused.length > 0 && (
        <Link className="banner refused" to={`/interventions/${refused[0]!.interventionId}`}>
          <Icon name="alert" size={18} />
          {refused.length > 1 ? `${refused.length} bons refusés : à vérifier` : "1 bon refusé : à vérifier"}
        </Link>
      )}
    </>
  );
}

/**
 * Nouvelle version : fenêtre au centre de l'écran pour que personne ne la rate.
 * « Plus tard » la referme et laisse une petite carte de rappel au-dessus de la barre du bas.
 */
function UpdatePrompt({ onUpdate }: { onUpdate: () => void }) {
  const [later, setLater] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nextVersion, setNextVersion] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    // Le serveur annonce déjà la nouvelle version : on l'affiche si on peut la connaître.
    api<{ version: string }>("/health")
      .then((h) => setNextVersion(h.version !== __APP_VERSION__ ? h.version : null))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (later) return;
    dialogRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setLater(true);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [later]);

  const update = () => {
    setBusy(true);
    onUpdate();
  };

  if (later) return <UpdateCard onUpdate={update} />;
  return createPortal(
    <div className="dialog-backdrop">
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef} tabIndex={-1}>
        <div className="dialog-icon">
          <Icon name="refresh" size={28} />
        </div>
        <h2 id={titleId}>Mise à jour disponible</h2>
        <p className="text-2">
          {nextVersion ? (
            <>
              La version <strong>{nextVersion}</strong> de TechPlanner est prête (vous avez la {__APP_VERSION__}).
            </>
          ) : (
            <>Une nouvelle version de TechPlanner est prête (vous avez la {__APP_VERSION__}).</>
          )}
        </p>
        <p className="small muted">L'application se recharge en quelques secondes. Les formulaires en cours sont gardés en brouillon.</p>
        <div className="dialog-actions">
          <Button variant="primary" size="large" block loading={busy} onClick={update}>
            Mettre à jour maintenant
          </Button>
          <Button variant="ghost" block disabled={busy} onClick={() => setLater(true)}>
            Plus tard
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Rappel discret après « Plus tard » : au-dessus de la barre du bas, jamais masqué. */
function UpdateCard({ onUpdate }: { onUpdate: () => void }) {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <div className="update-card" role="status">
      <Icon name="refresh" size={20} />
      <span className="grow">Nouvelle version disponible.</span>
      <button type="button" className="btn small primary" onClick={onUpdate}>
        Mettre à jour
      </button>
      <IconButton icon="close" label="Masquer le rappel" onClick={() => setHidden(true)} />
    </div>
  );
}

/** Remonte en haut de page à chaque changement d'écran (sauf retour arrière). */
export function ScrollRestorer() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
